// Shared with the backend (backend/src/patientportal/benefitsSummaryPdf), so
// this file must stay free of browser APIs and runtime imports.
import type {
  ChildBenefits,
  NetworkLimits,
  PlanBenefits,
  ServiceBenefit,
  ServiceKey,
} from "./benefitsSummary.contract";
import type {
  BenefitsSummaryData,
  NetworkAmounts,
  ServiceCost,
} from "./benefitsSummary.types";

export const BENEFITS_TEAM_PHONE = "(888) 307-2158 ext. 2";

// Shown for plan details and dates Salesforce did not return. A missing amount
// is shown as $0 instead (product decision).
export const NOT_AVAILABLE = "N/A";

const SERVICE_LABELS: Record<ServiceKey, string> = {
  medical: "Medical visits",
  // Named like the other ABA rows.
  aba: "ABA Individual Therapy",
  ot: "Occupational therapy",
  slp: "Speech therapy",
};

const currency = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

// Parsed by hand so a date-only value never shifts a day with the time zone.
function parseIsoDate(value: string | null): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value || "");
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

export function formatLongDate(value: string | null): string {
  const date = parseIsoDate(value);
  return date
    ? date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    : NOT_AVAILABLE;
}

function formatShortDate(date: Date, withYear: boolean): string {
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(withYear ? { year: "numeric" } : {}),
  });
}

// "Jun 1, 2026 to May 31, 2027", or "Jan 1 to Dec 31, 2026" within one year.
export function formatPlanYear(period: PlanBenefits["benefitPeriod"]): string {
  const start = parseIsoDate(period?.start ?? null);
  const end = parseIsoDate(period?.end ?? null);
  if (!start || !end) return NOT_AVAILABLE;
  const sameYear = start.getFullYear() === end.getFullYear();
  return `${formatShortDate(start, !sameYear)} to ${formatShortDate(end, true)}`;
}

export function formatCurrency(value: number | null): string {
  return currency.format(value ?? 0);
}

// Applied amounts keep their cents, as Silna shows them ("$39.92 met").
const formatMet = (applied: number | null | undefined) =>
  typeof applied === "number" ? `${formatRate(applied)} met` : undefined;

function toNetworkAmounts(limits: NetworkLimits | undefined): NetworkAmounts {
  const amounts: NetworkAmounts = {
    inNetwork: formatCurrency(limits?.inNetwork ?? null),
    outOfNetwork: formatCurrency(limits?.outOfNetwork ?? null),
  };
  const inNetworkMet = formatMet(limits?.inNetworkApplied);
  const outOfNetworkMet = formatMet(limits?.outOfNetworkApplied);
  if (inNetworkMet) amounts.inNetworkMet = inNetworkMet;
  if (outOfNetworkMet) amounts.outOfNetworkMet = outOfNetworkMet;
  return amounts;
}

// A service Silna marks self pay, or one out of network, is self pay: the
// plan's copay, coinsurance and limits don't apply (product decision). It
// shows its self-pay rate, or just "Self pay" without one, never a $0 the
// family won't pay. Otherwise a copay above zero is shown, then a coinsurance
// above zero, then a zero coinsurance when there is no copay at all, and
// otherwise the copay. A missing copay is shown as $0 (product decision).
// A hard visit limit is noted under the service, after any coinsurance note.
export function toServiceCost(
  service: ServiceBenefit,
  hardVisitLimit: number | null = null
): ServiceCost {
  const { copay, coinsurancePct, selfPayRate } = service;
  const network =
    service.inNetwork === null ? "unknown" : service.inNetwork ? "in" : "out";

  if (service.selfPay || network === "out") {
    const hasRate = typeof selfPayRate === "number" && selfPayRate > 0;
    return {
      id: service.key,
      service: SERVICE_LABELS[service.key],
      network,
      amount: hasRate ? formatRate(selfPayRate) : "Self pay",
      ...(hasRate ? { unit: "self pay" } : {}),
      selfPay: true,
    };
  }

  const hasCopay = copay !== null && copay > 0;
  const hasCoinsurance = coinsurancePct !== null && coinsurancePct > 0;
  const showCoinsurance =
    !hasCopay && (hasCoinsurance || (copay === null && coinsurancePct === 0));
  const amount = showCoinsurance ? `${coinsurancePct}%` : currency.format(copay ?? 0);
  const unit = showCoinsurance ? "coinsurance" : "copay";

  const notes = [
    hasCopay && hasCoinsurance ? `Plus ${coinsurancePct}% coinsurance after the copay.` : null,
    hardVisitLimit !== null
      ? `Covered up to ${hardVisitLimit} ${hardVisitLimit === 1 ? "appointment" : "appointments"} per year. After that, you may pay the full price.`
      : null,
  ].filter((note): note is string => note !== null);

  return {
    id: service.key,
    service: SERVICE_LABELS[service.key],
    network,
    amount,
    unit,
    note: notes.length ? notes.join(" ") : undefined,
  };
}

// Salesforce's therapy types for each service. PT has no row.
const THERAPY_TYPES: Partial<Record<ServiceKey, string>> = {
  aba: "ABA",
  ot: "OT",
  slp: "SLP",
};

// The lowest hard limit that covers the service, or null. Soft and authorized
// limits are not shown (product decision). A limit several therapies share is
// noted under each of them.
export function hardVisitLimitOf(
  key: ServiceKey,
  visitLimits: PlanBenefits["visitLimits"]
): number | null {
  const therapyType = THERAPY_TYPES[key];
  if (!therapyType) return null;
  const limits = visitLimits
    .filter((item) => item.hard && (item.therapyType || "").split(";").includes(therapyType))
    .map((item) => item.limit)
    .filter((limit): limit is number => typeof limit === "number" && limit > 0);
  return limits.length ? Math.min(...limits) : null;
}

type Row = { id: string; label: string };

// The rows listed around a service's own row: "before" rows go above it, the
// others after it. They cost the same as the service: its copay or
// coinsurance, or the self-pay rate (product decision). The ABA rows are
// ordered as on Silna's report: Assessments, Individual Therapy, Supervision,
// Parent Training. Silna calls the first one Initial Assessment; the portal
// says "ABA Assessments" (product decision).
const EXTRA_ROWS: Partial<Record<ServiceKey, { before: Row[]; after: Row[] }>> = {
  aba: {
    before: [{ id: "aba-assessment", label: "ABA Assessments" }],
    after: [
      { id: "aba-supervision", label: "ABA Supervision" },
      { id: "aba-parent-training", label: "ABA Parent Training" },
    ],
  },
  ot: { before: [], after: [{ id: "ot-evaluation", label: "Occupational therapy evaluation" }] },
  slp: { before: [], after: [{ id: "slp-evaluation", label: "Speech therapy evaluation" }] },
};

// Rates can have cents ("$93.11"), unlike the whole-dollar copays.
function formatRate(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(value);
}

// The service's extra rows, priced like the service. The hard visit limit
// note stays on the service's own row.
export function toExtraCosts(service: ServiceBenefit): { before: ServiceCost[]; after: ServiceCost[] } {
  const rows = EXTRA_ROWS[service.key];
  if (!rows) return { before: [], after: [] };
  const cost = toServiceCost(service);
  const toRow = ({ id, label }: Row) => ({ ...cost, id, service: label });
  return { before: rows.before.map(toRow), after: rows.after.map(toRow) };
}

const SERVICE_ORDER: ServiceKey[] = ["medical", "aba", "ot", "slp"];

const emptyService = (key: ServiceKey): ServiceBenefit => ({
  key,
  inNetwork: null,
  copay: null,
  coinsurancePct: null,
  referralRequired: false,
});

// Every service and rate row is always listed, in a fixed order, so families
// see the same table for every plan. Any amount Silna did not return is $0.
function toServiceCosts(
  services: ServiceBenefit[],
  visitLimits: PlanBenefits["visitLimits"]
): ServiceCost[] {
  return SERVICE_ORDER.flatMap((key) => {
    const service = services.find((item) => item.key === key) ?? emptyService(key);
    const { before, after } = toExtraCosts(service);
    return [...before, toServiceCost(service, hardVisitLimitOf(key, visitLimits)), ...after];
  });
}

export function childDisplayName(child: ChildBenefits): string {
  return [child.firstName, child.lastName].filter(Boolean).join(" ") || "your child";
}

// Met once what has been paid reaches the in-network limit. Silna has never
// returned out-of-network amounts.
const isMet = (limits: NetworkLimits | undefined) =>
  !!limits?.inNetwork &&
  typeof limits.inNetworkApplied === "number" &&
  limits.inNetworkApplied >= limits.inNetwork;

const hasOutOfNetwork = (limits: PlanBenefits["deductible"]) =>
  !!limits &&
  [limits.individual.outOfNetwork, limits.family.outOfNetwork].some((value) => value !== null);

// Maps one complete plan to the data the page and PDF render.
export function toBenefitsSummaryData(
  child: ChildBenefits,
  plan: PlanBenefits
): BenefitsSummaryData {
  const checkedOn = formatLongDate(plan.verifiedOn);
  const anyOutOfNetwork =
    hasOutOfNetwork(plan.deductible) || hasOutOfNetwork(plan.outOfPocketMax);

  return {
    childName: childDisplayName(child),
    checkedOn,
    checkedAt: checkedOn,
    insurance: {
      provider: plan.payerName || NOT_AVAILABLE,
      planName: plan.planName || NOT_AVAILABLE,
      memberId: plan.memberNumber || NOT_AVAILABLE,
      policyholder: plan.policyholderName || NOT_AVAILABLE,
    },
    referralRequired: plan.referralRequired,
    outOfPocketMaxMetNote: isMet(plan.outOfPocketMax?.family)
      ? "You've reached your family out-of-pocket maximum, so you're fully covered. No further payments are needed for the rest of this plan period."
      : undefined,
    services: toServiceCosts(plan.services, plan.visitLimits),
    deductible: toNetworkAmounts(plan.deductible?.individual),
    outOfPocketMax: toNetworkAmounts(plan.outOfPocketMax?.individual),
    familyDeductible: toNetworkAmounts(plan.deductible?.family),
    familyOutOfPocketMax: toNetworkAmounts(plan.outOfPocketMax?.family),
    outOfNetworkNote: anyOutOfNetwork
      ? undefined
      : "Your plan didn't list out-of-network amounts.",
    planYear: formatPlanYear(plan.benefitPeriod),
    benefitsTeamPhone: BENEFITS_TEAM_PHONE,
    previousSummaries: [],
  };
}

// Plans come ordered by PriorityOrder, at most one for each. A label is needed
// only to tell two or more plans apart. The server treats an unranked plan as
// the primary, so it is labelled that way too.
export function planLabel(plan: PlanBenefits, index: number): string {
  if (plan.priorityOrder === 1 || plan.priorityOrder === null) return "Primary";
  if (plan.priorityOrder === 2) return "Secondary";
  return plan.payerName || `Plan ${index + 1}`;
}
